import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import {
  playerCardIdentityDisplay,
  tierDisplayLabel,
  toTier,
  type PlayerCardProps,
} from "@bw/shared/components";

/** Player-native MUI renderer for the shared player identity contract. */
export function MaterialPlayerCard({
  userId,
  displayName,
  avatarUrl,
  rating,
  ratingDivision,
  homeFacilityName,
  relationship,
  onClickIdentity,
  leading,
  actions,
}: PlayerCardProps) {
  const identity = playerCardIdentityDisplay(displayName, userId);
  const tier = typeof rating === "string" ? toTier(rating) : null;
  const ratingDisplay = tier
    ? tierDisplayLabel(tier, ratingDivision ?? undefined)
    : rating != null
      ? String(rating)
      : "Unrated";

  const name = (
    <Stack
      component="span"
      direction="row"
      spacing={0.75}
      sx={{ minWidth: 0, alignItems: "baseline" }}
    >
      <Typography component="span" noWrap sx={{ minWidth: 0, fontWeight: 700 }}>
        {identity.name}
      </Typography>
      {identity.handle ? (
        <Typography component="span" variant="caption" color="text.secondary">
          {identity.handle}
        </Typography>
      ) : null}
    </Stack>
  );

  return (
    <Box
      component="article"
      role="group"
      aria-label={identity.name}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        width: "100%",
        py: 1,
      }}
    >
      {leading ? <Box sx={{ flexShrink: 0 }}>{leading}</Box> : null}

      <Avatar
        src={avatarUrl || undefined}
        alt={avatarUrl ? identity.name : undefined}
        aria-hidden={avatarUrl ? undefined : true}
        sx={{ width: 40, height: 40, flexShrink: 0 }}
      >
        {identity.initials}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {onClickIdentity ? (
          <ButtonBase
            aria-label={`View ${identity.name}`}
            onClick={onClickIdentity}
            sx={{ maxWidth: "100%", borderRadius: 1, textAlign: "left" }}
          >
            {name}
          </ButtonBase>
        ) : (
          name
        )}

        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 0.25, alignItems: "center", minWidth: 0 }}
        >
          {tier ? (
            <Chip
              label={ratingDisplay}
              size="small"
              variant="outlined"
              color="primary"
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {ratingDisplay}
            </Typography>
          )}
          {homeFacilityName ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {homeFacilityName}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      {relationship ? (
        <Chip label={relationship} size="small" variant="outlined" />
      ) : null}
      {actions ? (
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
}
